<template>
  <div class="w-full px-6 py-4">
    <div class="flex space-x-4 mb-6">
      <o-field-group class="w-full" :label="$t('general.inventory')" :info="$t('components.product.edit.inventory.info')">
        <text-field v-model="_product.inventory" :label="$t('general.title')" :disabled="true" />
        <div class="mt-3">
          <o-button flat @click="handleAddInventory">
            {{ $t('general.add') }}
          </o-button>
          <o-button flat @click="handleRemoveInventory">
            {{ $t('general.remove') }}
          </o-button>
        </div>
      </o-field-group>
    </div>

    <div>
      <h4 class="mb-3 font-medium text-gray-700 dark:text-gray-200">
        {{ $t('general.transactions') }}
      </h4>
      <o-table
        class="border rounded"
        :data="_product.inventoryTransactions"
        :headers="[
          {text: $t('general.status')},
          {text: $t('general.info')},
          {text: $t('general.dateTime')},
          {text: $t('components.product.edit.inventory.saleOrder')}
        ]"
        :limit="10"
        :tbody-class="{
          tfoot: '',
          tr: 'transition-all duration-200 bg-gray-50 dark:bg-gray-900',
          td: 'py-3 px-4 text-gray-800 text-sm dark:text-gray-100',
          stripe: 'bg-gray-50/50 dark:bg-gray-900'
        }"
        :thead-class="{
          thead: 'border-b',
          tr: 'border-blue-700',
          th: 'font-normal p-4 pb-3 bg-gray-50 text-gray-700 text-sm sticky top-0 dark:bg-gray-900 dark:text-gray-200',
          content: 'flex item-center'
        }"
        striped
      >
        <template #row="{ row, tdClass }">
          <td :class="tdClass">
            <o-chip :variant="row.amount > 0 ? 'success' : 'danger'" size="xs">
              {{ row.amount > 0 ? $t('components.product.edit.inventory.added') : $t('components.product.edit.inventory.removed') }}
            </o-chip>
          </td>
          <td :class="tdClass">
            {{ row.infoLabel }}
          </td>
          <td :class="tdClass">
            {{ formatDate(row.date) }}
          </td>
          <td :class="tdClass">
            <template v-if="row.order">
              #{{ row.order.orderNumber }}

              <o-panel-link :path="`/order/${row.order.idOrder}`" tag="o-button" size="sm" flat class="ml-3">
                {{ $t('general.view') }}
              </o-panel-link>
            </template>
            <template v-else>
              -
            </template>
          </td>
        </template>
      </o-table>
    </div>

    <add-inventory-dialog
      ref="addInventoryDialog"
      v-model:product="_product"
      @success="handleSuccess"
    />

    <remove-inventory-dialog
      ref="removeInventoryDialog"
      v-model:product="_product"
      @success="handleSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import RemoveInventory from '../actions/RemoveInventory.vue'
import AddInventoryDialog from '@/components/product/actions/AddInventory.vue'
import RemoveInventoryDialog from '@/components/product/actions/RemoveInventory.vue'

import {
  OCustomFields,
  OAvatar,
  OTable,
} from '@/components/elements'

import {
  TextField,
  CurrencyField,
  SelectField,
  NumberField,
  TextAreaField,
  ToggleField,
  SegmentField,
} from '@/components/fields'

const { $date, $bus } = useNuxtApp()

interface Props {
  product: Record<string, any>
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:product', value: any): void
}>()

const addInventoryDialog = ref<any>()
const removeInventoryDialog = ref<any>()

const _product = computed({
  get () {
    return props.product
  },
  set (value: any) {
    emit('update:product', value)
  },
})

const formatDate = (date: number) => {
  return $date.format($date.local(date * 1000), $date.presets.full)
}

const handleAddInventory = () => {
  addInventoryDialog.value.open()
}

const handleRemoveInventory = () => {
  removeInventoryDialog.value.open()
}

const handleSuccess = () => {
  $bus.emit.updated('Product', props.product)
}
</script>
